<template>
  <div class="bui-tbl-row-actions" data-testid="list-row-actions">
    <TblIconButton
      v-if="showEdit"
      :to="`/${apiPathsConfig?.edit}/${item.id}`"
      title="Edit"
      data-test-id="list-row-edit"
      :disabled="!editAllowed"
    >
      <PhPencilSimple :size="16" weight="bold" />
    </TblIconButton>

    <Dialog v-if="showCopy" panelClass="bui-dialog-panel">
      <template #expander="{ openModal }">
        <TblIconButton
          title="Copy"
          data-test-id="list-row-copy"
          :disabled="!copyAllowed"
          @click="copyAllowed && openModal()"
        >
          <PhCopy :size="16" weight="bold" />
        </TblIconButton>
      </template>
      <template #dialog.title>Warning</template>
      <template #dialog.content="{ closeModal, closeModalAfter }">
        <CopyItemConfirmationForm
          :itemId="item.id"
          @cancel="closeModal"
          @success="closeModalAfter(1000)"
        />
      </template>
    </Dialog>

    <Dialog v-if="showDelete" panelClass="bui-dialog-panel">
      <template #expander="{ openModal }">
        <TblIconButton
          title="Delete"
          data-test-id="list-row-delete"
          :disabled="!deleteAllowed"
          @click="deleteAllowed && openModal()"
        >
          <PhTrash :size="16" weight="bold" />
        </TblIconButton>
      </template>
      <template #dialog.title>Warning</template>
      <template #dialog.content="{ closeModal, closeModalAfter }">
        <DeleteItemConfirmationForm
          :itemId="item.id"
          @cancel="closeModal"
          @success="closeModalAfter(1000)"
        />
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject } from 'vue';
import { PhPencilSimple, PhTrash, PhCopy } from '@phosphor-icons/vue';
import TblIconButton from '../../atoms/TblIconButton/TblIconButton.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import DeleteItemConfirmationForm from '@/components/ListingPages/ItemsLister/ItemsList/ItemActionsButtons/DeleteItem/DeleteItemConfirmationForm/DeleteItemConfirmationForm.vue';
import CopyItemConfirmationForm from '@/components/ListingPages/ItemsLister/ItemsList/ItemActionsButtons/CopyItem/CopyItemConfirmationForm/CopyItemConfirmationForm.vue';
import useUser from '@/composables/user';

const props = defineProps<{
  item: any;
}>();

const user = useUser();
const itemsListHelpers = inject<any>('itemsListHelpers');
const listerConfig = inject<any>('listerConfig', {});

const apiPathsConfig = computed(() => itemsListHelpers?.apiPathsConfig ?? {});
const availableActions = computed(() => listerConfig?.availableActionsConfig ?? {});
const areaAccess = computed(() => listerConfig?.areaAccess ?? {});

const showEdit = computed(() => availableActions.value.edit !== false);
const showCopy = computed(() => !!availableActions.value.copy);
const showDelete = computed(() => availableActions.value.delete !== false);

const editAllowed = computed(
  () =>
    !!areaAccess.value?.hasWriteAccess &&
    user.hasWriteAccessToItem(props.item) &&
    user.hasWriteAccessToAssetType(props.item?.assetType),
);

const copyAllowed = computed(
  () =>
    !!areaAccess.value?.hasCreateAccess &&
    user.hasWriteAccessToItem(props.item) &&
    user.hasCreateAccessToAssetType(props.item?.assetType),
);

const deleteAllowed = computed(
  () =>
    !!areaAccess.value?.hasDeleteAccess &&
    user.hasDeleteAccessToItem(props.item) &&
    user.hasDeleteAccessToAssetType(props.item?.assetType),
);
</script>

<style lang="scss" scoped>
.bui-tbl-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>
